import React, { useEffect, useState } from 'react';
import { CheckCircle2, Loader2, Save, X } from 'lucide-react';
import { FRIST_KATEGORIEN, FristKategorie, SifaFrist } from '../../services/sifa/betreuungTypes';
import { createFrist, erledigeFrist, updateFrist } from '../../services/sifa/sifaBetreuungService';
import { fetchMitarbeiter } from '../../services/sifa/sifaService';
import { SifaMitarbeiter } from '../../services/sifa/types';
import { describeError } from '../../services/errorMessage';
import { sortiereMitarbeiter } from './mitarbeiterModulLogic';
import { formatDatum, heuteIso } from './unterweisungModulLogic';
import {
  FristEntwurf, entwurfAusFrist, entwurfZuNeueFrist, intervallVorschlag, leererFristEntwurf, pruefeFristEntwurf, wiederholungText,
} from './fristenModulLogic';

interface Props {
  projectId: string;
  /** null = neu anlegen. */
  frist: SifaFrist | null;
  /** Vorbelegung beim Anlegen (ASA-Vorschlag); wird von `frist` uebersteuert. */
  vorlage?: FristEntwurf | null;
  isReadOnly?: boolean;
  onClose: () => void;
  onGespeichert: (f: SifaFrist) => void;
}

const FELD = 'w-full px-3 py-2 min-h-[40px] text-sm text-slate-800 bg-slate-50 rounded-lg outline-none focus:bg-white focus:ring-1 focus:ring-amber-300 disabled:text-slate-500';
const LABEL = 'block text-[11px] font-bold uppercase text-slate-500 mb-1';

/**
 * Anlegen/Bearbeiten einer Frist.
 *
 * Die Kategorie bringt ihren Intervall-Vorschlag mit (FRIST_KATEGORIEN),
 * aber nur solange der Nutzer das Feld nicht selbst angefasst hat — wer
 * „Prüfung" mit 6 Monaten eintippt und dann die Kategorie wechselt, soll
 * seine 6 nicht verlieren. Der Mitarbeiterstamm wird beim Oeffnen geladen
 * (fail-open: ohne Stamm bleibt die Person-Auswahl einfach leer).
 */
export const FristDialog: React.FC<Props> = ({ projectId, frist, vorlage = null, isReadOnly = false, onClose, onGespeichert }) => {
  const [e, setE] = useState<FristEntwurf>(() => (frist ? entwurfAusFrist(frist) : vorlage || leererFristEntwurf(heuteIso())));
  const [intervallManuell, setIntervallManuell] = useState(!!frist || !!vorlage);
  const [mitarbeiter, setMitarbeiter] = useState<SifaMitarbeiter[]>([]);
  const [speichert, setSpeichert] = useState(false);
  const [fehler, setFehler] = useState<string[]>([]);

  useEffect(() => {
    let aktiv = true;
    fetchMitarbeiter(projectId)
      .then((m) => { if (aktiv) setMitarbeiter(sortiereMitarbeiter(m.filter((x) => !x.zusammengefuehrtIn))); })
      .catch(() => { /* ohne Stamm keine Personenwahl — kein Fehler */ });
    return () => { aktiv = false; };
  }, [projectId]);

  const setze = <K extends keyof FristEntwurf>(k: K, v: FristEntwurf[K]) => setE((prev) => ({ ...prev, [k]: v }));

  const kategorieWechseln = (k: FristKategorie) => {
    setE((prev) => ({
      ...prev,
      kategorie: k,
      intervallMonate: intervallManuell ? prev.intervallMonate : String(intervallVorschlag(k) ?? ''),
    }));
  };

  const speichern = async () => {
    if (isReadOnly || speichert) return;
    const probleme = pruefeFristEntwurf(e);
    if (probleme.length > 0) { setFehler(probleme); return; }
    setSpeichert(true);
    setFehler([]);
    try {
      const eingabe = entwurfZuNeueFrist(e);
      const gespeichert = frist ? await updateFrist(frist.id, eingabe) : await createFrist(projectId, eingabe);
      onGespeichert(gespeichert);
    } catch (err) {
      setFehler([describeError(err)]);
    } finally {
      setSpeichert(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[95vh] flex flex-col" onClick={(ev) => ev.stopPropagation()}>
        <div className="p-4 border-b border-slate-200 flex items-center justify-between shrink-0">
          <h3 className="text-sm font-bold text-slate-800">{frist ? (isReadOnly ? 'Frist' : 'Frist bearbeiten') : 'Frist anlegen'}</h3>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full"><X className="w-4 h-4 text-slate-500" /></button>
        </div>
        <div className="p-4 space-y-3 overflow-y-auto">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className={LABEL}>Kategorie</label>
              <select value={e.kategorie} onChange={(ev) => kategorieWechseln(ev.target.value as FristKategorie)} disabled={isReadOnly} className={FELD}>
                {FRIST_KATEGORIEN.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
              </select>
            </div>
            <div className="col-span-2">
              <label className={LABEL}>Titel</label>
              <input value={e.titel} onChange={(ev) => setze('titel', ev.target.value)} disabled={isReadOnly} className={FELD} placeholder="z. B. DGUV V3 Prüfung ortsveränderliche Geräte" autoFocus={!frist} />
            </div>
            <div>
              <label className={LABEL}>Fällig am</label>
              <input type="date" value={e.faelligAm} onChange={(ev) => setze('faelligAm', ev.target.value)} disabled={isReadOnly} className={FELD} />
            </div>
            <div>
              <label className={LABEL}>Wiederholung (Monate)</label>
              <input
                inputMode="numeric"
                value={e.intervallMonate}
                onChange={(ev) => { setIntervallManuell(true); setze('intervallMonate', ev.target.value); }}
                disabled={isReadOnly}
                className={FELD}
                placeholder="leer = einmalig"
              />
              <p className="text-[11px] text-slate-500 mt-1">{wiederholungText(Number(e.intervallMonate) || null)}{!isReadOnly && e.intervallMonate ? ' — beim Erledigen entsteht die nächste Frist' : ''}</p>
            </div>
            <div className="col-span-2">
              <label className={LABEL}>Person (Mitarbeiterstamm)</label>
              <select value={e.mitarbeiterId} onChange={(ev) => setze('mitarbeiterId', ev.target.value)} disabled={isReadOnly} className={FELD}>
                <option value="">— keine —</option>
                {mitarbeiter.map((m) => (
                  <option key={m.id} value={m.id}>{m.nachname}, {m.vorname}{m.abteilung ? ` (${m.abteilung})` : ''}</option>
                ))}
                {frist?.mitarbeiterId && !mitarbeiter.some((m) => m.id === frist.mitarbeiterId) && (
                  <option value={frist.mitarbeiterId}>(Person nicht mehr im Stamm)</option>
                )}
              </select>
            </div>
            <div className="col-span-2">
              <label className={LABEL}>Zuständig im Betrieb</label>
              <input value={e.zustaendig} onChange={(ev) => setze('zustaendig', ev.target.value)} disabled={isReadOnly} className={FELD} placeholder="z. B. Hausmeister, Frau Meier" />
            </div>
            <div className="col-span-2">
              <label className={LABEL}>Notiz</label>
              <textarea value={e.notiz} onChange={(ev) => setze('notiz', ev.target.value)} disabled={isReadOnly} rows={3} className={`${FELD} min-h-[72px]`} />
            </div>
          </div>

          {frist?.erledigtAm && (
            <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">Erledigt am {formatDatum(frist.erledigtAm)}.</p>
          )}
          {fehler.length > 0 && (
            <ul className="text-xs text-red-700 space-y-0.5">{fehler.map((f) => <li key={f}>{f}</li>)}</ul>
          )}
        </div>
        {!isReadOnly && (
          <div className="p-4 border-t border-slate-200 flex justify-end gap-2 shrink-0">
            <button onClick={onClose} className="min-h-[40px] px-4 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-xl">Abbrechen</button>
            <button onClick={() => void speichern()} disabled={speichert} className="min-h-[40px] px-4 flex items-center gap-2 text-sm font-bold text-white bg-amber-600 hover:bg-amber-700 rounded-xl disabled:opacity-40">
              {speichert ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Speichern
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

interface ErledigenProps {
  frist: SifaFrist;
  onClose: () => void;
  /** Nach dem Erledigen: die abgeschlossene Zeile und — bei Wiederholung — die neu angelegte. */
  onErledigt: (erledigt: SifaFrist, naechste: SifaFrist | null) => void;
}

/**
 * „Erledigt" mit Datum (heute vorbelegt). Der Service legt bei einem
 * Intervall die naechste Zeile an (vorgaenger_id); die Meldung dazu zeigt
 * das Modul.
 */
export const FristErledigenDialog: React.FC<ErledigenProps> = ({ frist, onClose, onErledigt }) => {
  const [datum, setDatum] = useState(heuteIso());
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const bestaetigen = async () => {
    if (laeuft) return;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(datum)) { setFehler('Ein Datum ist nötig.'); return; }
    setLaeuft(true);
    setFehler(null);
    try {
      const r = await erledigeFrist(frist.id, datum);
      onErledigt(r.erledigt, r.naechste);
    } catch (err) {
      setFehler(describeError(err));
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm" onClick={(ev) => ev.stopPropagation()}>
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-sm font-bold text-slate-800">Frist erledigen</h3>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full"><X className="w-4 h-4 text-slate-500" /></button>
        </div>
        <div className="p-4 space-y-3">
          <p className="text-sm text-slate-700 font-semibold">{frist.titel}</p>
          <p className="text-xs text-slate-500">Fällig am {formatDatum(frist.faelligAm)} · {wiederholungText(frist.intervallMonate)}</p>
          <div>
            <label className={LABEL}>Erledigt am</label>
            <input type="date" value={datum} onChange={(ev) => setDatum(ev.target.value)} className={FELD} autoFocus />
          </div>
          {frist.intervallMonate ? (
            <p className="text-xs text-slate-600">Die nächste Fälligkeit wird automatisch angelegt ({wiederholungText(frist.intervallMonate)}).</p>
          ) : null}
          {fehler && <p className="text-xs text-red-700">{fehler}</p>}
        </div>
        <div className="p-4 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="min-h-[40px] px-4 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-xl">Abbrechen</button>
          <button onClick={() => void bestaetigen()} disabled={laeuft} className="min-h-[40px] px-4 flex items-center gap-2 text-sm font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl disabled:opacity-40">
            {laeuft ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Erledigt
          </button>
        </div>
      </div>
    </div>
  );
};
